import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import Form from "../carbon/Form/Form";
import AddressRow from "./AddressRow";
import {
  AddressesFormFields,
  AddressFormFields,
  addressesValidationSchema,
} from "./addressesValidationSchema";
import {
  CompanyProfileRequest,
  CompanyProfileResponse,
  CountryResponse,
} from "../../types";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import "./addressesForm.scss";

interface AddressesFormProps {
  profile: CompanyProfileResponse;
  countries: CountryResponse[];
  setIsDirty: (isDirty: boolean) => void;
}

const emptyAddress: AddressFormFields = {
  street: "",
  city: "",
  state: "",
  postalCode: "",
  countryId: "",
  isLegalAddress: false,
  isDeliverySite: false,
};

/**
 * The Addresses wizard sub-form (`PUT /company/{id}/profile`), rebuilt on Carbon + React Hook
 * Form's `useFieldArray` (Task 8, Profile/Company flow) in place of 340 lines of Tailwind and a
 * two-stage "stage a draft locally, then merge with the already-saved list on Save" flow.
 *
 * Per Task 3's own primitives gap-check (which this task's brief points to): "Tasks 7-10 will
 * almost certainly replace the two-stage ... flow with a single RHF-managed array field per
 * Carbon/RHF conventions the migration already established elsewhere." That's what this is — ONE
 * `addresses` array, seeded from `profile.addresses`, editable in place via `useFieldArray`
 * (`append`/`remove`), submitted whole. This is a real behaviour change from the pre-Carbon form
 * (which only ever let you ADD new rows or remove an unsaved one — an already-persisted address
 * was permanently read-only), but a safe one: `CompanyService#updateCompanyAddresses` (backend)
 * already fully REPLACES the address list on every save (`company.getAddresses().clear()` then
 * re-adds from the request) — the pre-Carbon form's "existing rows are read-only" behaviour was a
 * FRONTEND-only restriction with no backend counterpart, not a safety rule this rewrite needs to
 * preserve.
 *
 * `onSubmit` still spreads `{...profile, ...}` (not just `{addresses}`) for the same reason as
 * `BasicInformationForm.tsx`: `taxId` is now `@NotBlank` on the backend (Task 2), and every wizard
 * sub-form's independent save must keep carrying it.
 */
const AddressesForm = ({
  profile,
  countries,
  setIsDirty,
}: AddressesFormProps) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const defaultValues: AddressesFormFields = useMemo(
    () => ({
      addresses: (profile.addresses || []).map((address) => ({
        id: address.id,
        street: address.street ?? "",
        city: address.city ?? "",
        state: address.state ?? "",
        postalCode: address.postalCode ?? "",
        countryId: address.countryId ?? "",
        isLegalAddress: Boolean(address.isLegalAddress),
        isDeliverySite: Boolean(address.isDeliverySite),
      })),
    }),
    [profile]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<AddressesFormFields>({
    resolver: yupResolver(addressesValidationSchema),
    defaultValues,
  });

  // Same re-sync + one-directional dirty-forwarding precedent as BasicInformationForm.tsx — see
  // that file's own docstring/comments for the full rationale (a successful save invalidates
  // `useCompanyProfile`, which lands a fresh `profile` here and re-baselines via `reset`).
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (isDirty) {
      setIsDirty(true);
    }
  }, [isDirty, setIsDirty]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "addresses",
  });

  const { mutate: updateProfile, isPending: isSaving } =
    useUpdateCompanyProfile(() => {
      setIsDirty(false);
    });

  const onSubmit = (data: AddressesFormFields) => {
    updateProfile({
      ...profile,
      companyId: user?.company?.id,
      addresses: data.addresses,
    } as CompanyProfileRequest);
  };

  return (
    <div className="addresses-form">
      <div className="addresses-form__header">
        <h3>{t("companyProfile.addresses")}</h3>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          type="button"
          onClick={() => append(emptyAddress)}
        >
          {t("companyProfile.addAddress")}
        </Button>
      </div>

      {isSaving ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="addresses-form__form"
        >
          {fields.length > 0 && (
            <div className="addresses-form__rows">
              {fields.map((field, index) => (
                <AddressRow
                  key={field.id}
                  index={index}
                  control={control}
                  countries={countries}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}

          <div className="addresses-form__actions">
            <Button type="submit" disabled={isSaving || !isDirty}>
              {t("cta.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default AddressesForm;
